import { suggestCategoryCode, validateCategoryCode } from "./categoryCode";
import { nextInternalCode } from "./idGenerators";

// Pure. Given current data and a typed category NAME, return the (possibly extended)
// categories list and the PREFIX to mint an internalCode under — no code minting here, so a
// caller minting several items in one batch (App.js's commitPendingItems) can resolve each
// item's category through this function while doing its OWN single upfront
// maxCodeNumbersByPrefix scan + local per-prefix counter, instead of re-scanning the full data
// object once per item the way calling resolveItemCreate (below) per item would.
//
// options.explicitCode: when creating a NEW category, honor this code instead of suggesting one.
// If it collides or is invalid, returns { error } without creating the category or falling back.
// Existing-category and empty-name paths ignore explicitCode entirely.
// Success always returns error: null (added for forward use by callers that check it).
export function resolveCategoryForCreate(data, categoryName, options = {}) {
  const cats  = data.categories || [];
  const name  = (categoryName || "").trim();
  if (!name) {
    // Data-layer enforcement to match the two UI-layer gates (ItemFormModal, SetNameModal) that
    // already require a category — previously fell through to an "IJM" fallback prefix here.
    return { categories: cats, prefix: null, error: "Kategori wajib diisi." };
  }
  let categories = cats;
  let prefix;
  const found = cats.find(
    (c) => (c.name || "").trim().toUpperCase() === name.toUpperCase()
  );
  if (found) {
    // Existing category: use its code; explicitCode is ignored.
    prefix = found.code;
  } else {
    const { explicitCode } = options;
    if (explicitCode && String(explicitCode).trim()) {
      // Caller supplied an explicit code for the new category — validate it.
      const norm = String(explicitCode).trim().toUpperCase();
      const err = validateCategoryCode(norm, cats.map((c) => c.code));
      if (err) {
        return { categories: cats, prefix: null, error: err };
      }
      // Tidy the name (collapse internal whitespace) to match App.js createCategory.
      const tidiedName = name.replace(/\s+/g, " ");
      categories = [...cats, { name: tidiedName, code: norm }];
      prefix = norm;
    } else {
      // No explicit code: suggest one as before.
      const code = suggestCategoryCode(name, cats.map((c) => c.code));
      categories = [...cats, { name, code }];
      prefix = code;
    }
  }
  return { categories, prefix, error: null };
}

// Thin wrapper: resolve the category, then mint a single code against the WHOLE `data` object
// (item-code-reuse fix — a retired code still referenced by a transaction, history entry,
// recipe, or transformation must never be reissued, not just one still present in
// `data.items`). One full scan per call — correct and cheap for a single-item mint (this
// function's only real callers: saveItem, previewItemCreate — each minting exactly one item
// per human action). A caller minting several items in one batch should use
// resolveCategoryForCreate directly instead, alongside its own upfront-scanned counter (see
// App.js's commitPendingItems).
export function resolveItemCreate(data, categoryName, options = {}) {
  const { categories, prefix, error } = resolveCategoryForCreate(data, categoryName, options);
  if (error) return { categories, internalCode: null, error };
  const internalCode = nextInternalCode(data, prefix);
  return { categories, internalCode, error: null };
}
